import { Injectable, OnDestroy, isDevMode } from '@angular/core';
import { AuthService } from './auth.service';
import { TokenRefreshCoordinator, RefreshCooldownError, StaleSessionError } from './token-refresh-coordinator.service';
import { TokenRefreshMetricsService } from './token-refresh-metrics.service';
import { AuthSessionTerminatorService } from './auth-session-terminator.service';
import { Subscription, firstValueFrom } from 'rxjs';
import { filter } from 'rxjs/operators';
import { isTerminalAuthFailure } from '../utils/http-status.util';

/**
 * Service to automatically refresh access tokens before they expire.
 * Uses JWT expiration claims for accurate timing.
 * 
 * Key improvements:
 * - Reads actual token expiration from JWT
 * - Handles page visibility changes
 * - Prevents race conditions with single-flight pattern
 * - Accounts for timer drift
 * - Coordinates with AuthInterceptor
 * - Metrics tracking
 *
 * Retries, backoff and the decision that a session is over do not live here: the refresh itself is
 * the TokenRefreshCoordinator's (one attempt shared by every caller, with its own cooldown), and
 * ending a session is the AuthSessionTerminatorService's. This service only decides *when* to ask.
 * 
 * Token lifetimes (fallback if JWT decoding fails):
 * - Production: 15 minutes (refresh at 13.5 minutes)
 * - Development: 60 minutes (refresh at 54 minutes)
 */
@Injectable({
  providedIn: 'root',
})
export class TokenRefreshTimerService implements OnDestroy {
  private refreshTimer: ReturnType<typeof setTimeout> | null = null;
  private tokenExpiresAt: number | null = null;
  private authSubscription: Subscription | null = null;
  private visibilitySubscription: Subscription | null = null;
  // Fire a refresh when this fraction of the total token lifetime remains (20%)
  private readonly REFRESH_THRESHOLD = 0.8; // 1 - 0.8 = 20% of lifetime triggers refresh
  // Minimum safety gap: never fire closer than this to expiry, and never sooner than this from now
  private readonly MIN_REFRESH_INTERVAL = 5000; // 5 seconds
  private readonly MIN_TIME_BEFORE_REFRESH = 10000; // 10 seconds - refresh immediately if less remains
  private readonly IMMEDIATE_REFRESH_THRESHOLD_MS = 5000; // 5 seconds - refresh immediately if expiring this soon

  // A refresh that completed without moving the expiry this timer reads — see noteRefreshOutcome().
  private hollowRefreshStreak = 0;
  private hollowRetryNotBefore = 0;
  private readonly MAX_HOLLOW_REFRESH_BACKOFF_MS = 5 * 60 * 1000;
  private readonly HOLLOW_REFRESH_ALARM_AT = 3;

  // Token lifetimes in milliseconds (fallback)
  private readonly TOKEN_LIFETIME_MS: number;
  private readonly REFRESH_DELAY_MS: number;
  private readonly MIN_TIME_REMAINING_TO_RECALCULATE: number; // 20% of typical token lifetime

  constructor(
    private authService: AuthService,
    private coordinator: TokenRefreshCoordinator,
    private metrics: TokenRefreshMetricsService,
    private terminator: AuthSessionTerminatorService
  ) {
    // Get token lifetime based on environment (fallback if JWT decoding fails)
    const tokenLifetimeMinutes = isDevMode() ? 60 : 15;
    this.TOKEN_LIFETIME_MS = tokenLifetimeMinutes * 60 * 1000;
    this.REFRESH_DELAY_MS = this.TOKEN_LIFETIME_MS * this.REFRESH_THRESHOLD;
    this.MIN_TIME_REMAINING_TO_RECALCULATE = this.TOKEN_LIFETIME_MS * 0.2; // 20% of typical token lifetime

    // Start timer immediately if user is already authenticated (e.g., page refresh)
    if (this.authService.isAuthenticated()) {
      this.initializeTimer();
    }

    // Start timer when user becomes authenticated
    this.authSubscription = this.authService.currentUser$
      .pipe(filter((user) => user !== null))
      .subscribe(() => {
        this.initializeTimer();
      });

    // Reset timer when user logs out
    this.authSubscription.add(
      this.authService.currentUser$
        .pipe(filter((user) => user === null))
        .subscribe(() => {
          this.stopTimer();
          this.coordinator.reset();
          // A new login starts with a clean slate; the backoff belonged to the old one.
          this.hollowRefreshStreak = 0;
          this.hollowRetryNotBefore = 0;
          this.metrics.reset();
        })
    );

    // Handle page visibility changes (tab switching, browser sleep)
    this.setupVisibilityHandling();
    
    // Listen for token refreshes from other tabs
    this.setupCrossTabRefreshListener();
  }

  ngOnDestroy(): void {
    this.stopTimer();
    if (this.authSubscription) {
      this.authSubscription.unsubscribe();
    }
    if (this.visibilitySubscription) {
      this.visibilitySubscription.unsubscribe();
    }
  }

  /**
   * Re-initialize timer with fresh expiration from AuthService.
   * Call this after token refresh (e.g. from AuthInterceptor) to ensure timer uses new token expiration.
   */
  restartTimer(): void {
    if (this.authService.isAuthenticated()) {
      this.initializeTimer();
    }
  }

  /**
   * Log only in development mode to reduce production noise
   */
  private log(...args: unknown[]): void {
    if (isDevMode()) {
      console.log(...args);
    }
  }

  /**
   * Initialize timer by reading token expiration from JWT or AuthService
   * Falls back to configured timer when tokens are in httpOnly cookies (normal production flow)
   */
  private initializeTimer(): void {
    // Nothing to schedule once the session is over, and eight different things call this.
    //
    // Without the guard the timer re-armed after every rejected refresh: the terminator had already
    // ended the session, so `end()` returned "already over" without doing anything, the delay for an
    // expired token came out negative and was clamped up to the five-second floor, and the tab sent
    // POST /auth/refresh every five seconds for as long as it stayed open — each one a 401, each one
    // a red line in the console. Observed on dev as "Refresh attempt #2, #3, #4, #5 …" at a steady
    // five-second cadence. The next login re-arms it, because that calls terminator.reset().
    if (this.terminator.isSessionOver()) {
      this.log('[TokenRefreshTimer] Session is over; not scheduling a refresh');
      this.stopTimer();
      return;
    }

    // Log token expiration info on page load
    this.logTokenExpirationInfo();

    // First, try to get expiration time from AuthService (from login/refresh response)
    const expiresAt = this.authService.getAccessTokenExpiresAt();
    const refreshExpiresAt = this.authService.getRefreshTokenExpiresAt();
    
    if (expiresAt) {
      this.tokenExpiresAt = expiresAt.getTime();
      this.log('[TokenRefreshTimer] Using expiration time from AuthService:', new Date(this.tokenExpiresAt));
      
      // Check if token is already expired or very close to expiration
      const now = Date.now();
      const timeUntilExpiration = this.tokenExpiresAt - now;
      
      // If token is expired or expires within threshold, refresh immediately
      if (timeUntilExpiration <= this.IMMEDIATE_REFRESH_THRESHOLD_MS) {
        // Check if refresh token is still valid
        if (refreshExpiresAt && refreshExpiresAt.getTime() > now) {
          this.log('[TokenRefreshTimer] Access token expired or expiring soon, refreshing immediately');
          this.refreshToken();
          return;
        } else {
          console.warn('[TokenRefreshTimer] Access token expired and refresh token also expired, cannot refresh');
          this.stopTimer();
          return;
        }
      }
      
      // If token is expired, refresh immediately (already handled above)
      // Otherwise, calculate when to refresh based on REFRESH_THRESHOLD
      // REFRESH_THRESHOLD = 0.8 means we refresh when 80% of lifetime has passed (20% remaining)
      // For a 60-second token, we refresh at 48 seconds (when 12 seconds remain)
      // Calculate the refresh time: tokenExpiresAt - (tokenLifetime * (1 - REFRESH_THRESHOLD))
      // But we don't have tokenLifetime reliably, so we use a simpler approach:
      // If less than 20% of a typical token lifetime remains, refresh immediately
      if (timeUntilExpiration <= this.MIN_TIME_BEFORE_REFRESH) {
        // Very close to expiration, refresh immediately
        this.log('[TokenRefreshTimer] Token is very close to expiration, refreshing immediately');
        this.refreshToken();
        return;
      }
      
      this.startTimer();
      return;
    }

    // No expiration from AuthService - use fallback timer (e.g. tokens in httpOnly cookies)
    this.log('[TokenRefreshTimer] No access token expiration available, using fallback timer');
    this.startTimerWithFallback();
  }

  /**
   * Start the automatic refresh timer based on actual token expiration.
   *
   * Scheduling formula:
   *   delay = timeRemaining - tokenLifetime * 0.20
   *
   * Example: lifetime = 200 s, open with 100 s remaining
   *   → delay = 100 - 200 * 0.2 = 60 s → fires with 40 s left ✓
   *
   * Fallback (no issuedAt available): fires at 80 % of remaining time.
   */
  startTimer(): void {
    const savedExpiresAt = this.tokenExpiresAt;
    this.stopTimer();
    this.tokenExpiresAt = savedExpiresAt;

    if (!this.authService.isAuthenticated()) {
      return;
    }

    if (!this.tokenExpiresAt) {
      console.warn('[TokenRefreshTimer] No token expiration time available');
      return;
    }

    const now = Date.now();
    const timeUntilExpiration = this.tokenExpiresAt - now;

    if (timeUntilExpiration <= 0) {
      console.warn('[TokenRefreshTimer] Token already expired, refreshing immediately');
      this.refreshToken();
      return;
    }

    // Preferred: derive delay from full token lifetime so we always fire at 20 % remaining
    const issuedAt = this.authService.getAccessTokenIssuedAt();
    let delay: number;

    if (issuedAt) {
      const tokenLifetimeMs = this.tokenExpiresAt - issuedAt.getTime();
      const earlyFireMs = tokenLifetimeMs * (1 - this.REFRESH_THRESHOLD); // 20 % of lifetime
      delay = timeUntilExpiration - earlyFireMs;
      this.log(
        `[TokenRefreshTimer] lifetime=${Math.floor(tokenLifetimeMs / 1000)}s, ` +
        `fire buffer=${Math.floor(earlyFireMs / 1000)}s, ` +
        `raw delay=${Math.floor(delay / 1000)}s`
      );
    } else {
      // Fallback: fire at REFRESH_THRESHOLD % of remaining time
      delay = Math.floor(timeUntilExpiration * this.REFRESH_THRESHOLD);
      this.log('[TokenRefreshTimer] No issuedAt available - fallback: 80% of remaining time');
    }

    // Safety clamps:
    //   upper: never schedule past (expiry - MIN_REFRESH_INTERVAL) so we fire before the token dies
    //   lower: never fire sooner than MIN_REFRESH_INTERVAL from now (avoids immediate back-to-back calls)
    const maxDelay = timeUntilExpiration - this.MIN_REFRESH_INTERVAL;
    const actualDelay = Math.max(Math.min(delay, maxDelay), this.MIN_REFRESH_INTERVAL);

    if (actualDelay <= 0) {
      this.log('[TokenRefreshTimer] Effective delay <= 0, refreshing immediately');
      this.refreshToken();
      return;
    }

    this.log(
      `[TokenRefreshTimer] Starting timer: refresh in ${Math.floor(actualDelay / 1000)}s ` +
      `(token expires in ${Math.floor(timeUntilExpiration / 1000)}s)`
    );

    this.refreshTimer = setTimeout(() => {
      this.refreshToken();
    }, actualDelay);
  }

  /**
   * Fallback: Start timer with configured values if JWT decoding fails or tokens are in httpOnly cookies
   * Note: We can't read the actual token expiration from httpOnly cookies,
   * so we use the configured lifetime as a reasonable estimate
   */
  private startTimerWithFallback(): void {
    const delay = this.REFRESH_DELAY_MS;

    this.log(`[TokenRefreshTimer] Using fallback timer: will refresh in ${Math.floor(delay / 1000)} seconds (${this.TOKEN_LIFETIME_MS / 1000 / 60} minutes lifetime)`);

    this.refreshTimer = setTimeout(() => {
      this.refreshToken();
    }, delay);
  }

  /**
   * Stop the refresh timer
   */
  stopTimer(): void {
    if (this.refreshTimer) {
      clearTimeout(this.refreshTimer);
      this.refreshTimer = null;
    }
    this.tokenExpiresAt = null;
  }

  /**
   * Log token expiration information on page load
   */
  private logTokenExpirationInfo(): void {
    const accessTokenExpiresAt = this.authService.getAccessTokenExpiresAt();
    const refreshTokenExpiresAt = this.authService.getRefreshTokenExpiresAt();
    
    this.log('---------- token expiration ----------');
    
    if (accessTokenExpiresAt) {
      const now = Date.now();
      const expiresAt = accessTokenExpiresAt.getTime();
      const timeUntilExpiration = expiresAt - now;
      const minutesUntilExpiration = Math.floor(timeUntilExpiration / 60000);
      const secondsUntilExpiration = Math.floor((timeUntilExpiration % 60000) / 1000);
      
      this.log('Access token expires at:', accessTokenExpiresAt.toISOString());
      this.log(`Access token expires in: ${minutesUntilExpiration}m ${secondsUntilExpiration}s`);
      
      // Calculate next refresh time: fire at 20% of token lifetime remaining
      const issuedAt = this.authService.getAccessTokenIssuedAt();
      let timeUntilRefresh: number;
      if (issuedAt) {
        const tokenLifetimeMs = expiresAt - issuedAt.getTime();
        const earlyFireMs = tokenLifetimeMs * (1 - this.REFRESH_THRESHOLD); // 20 % of lifetime
        timeUntilRefresh = timeUntilExpiration - earlyFireMs;
      } else {
        timeUntilRefresh = timeUntilExpiration * this.REFRESH_THRESHOLD;
      }
      const nextRefreshDate = new Date(now + timeUntilRefresh);
      const minutesUntilRefresh = Math.floor(timeUntilRefresh / 60000);
      const secondsUntilRefresh = Math.floor((timeUntilRefresh % 60000) / 1000);
      
      if (timeUntilRefresh > 0) {
        this.log(`Next refresh on: ${nextRefreshDate.toISOString()} (in ${minutesUntilRefresh}m ${secondsUntilRefresh}s)`);
      } else {
        this.log('Next refresh: IMMEDIATELY (token expired or close to expiration)');
      }
    } else {
      this.log('Access token expires at: Not available');
      this.log('Next refresh on: Not available');
    }
    
    if (refreshTokenExpiresAt) {
      const now = Date.now();
      const expiresAt = refreshTokenExpiresAt.getTime();
      const timeUntilExpiration = expiresAt - now;
      const minutesUntilExpiration = Math.floor(timeUntilExpiration / 60000);
      const secondsUntilExpiration = Math.floor((timeUntilExpiration % 60000) / 1000);
      
      this.log('Refresh token expires at:', refreshTokenExpiresAt.toISOString());
      this.log(`Refresh token expires in: ${minutesUntilExpiration}m ${secondsUntilExpiration}s`);
    } else {
      this.log('Refresh token expires at: Not available');
    }
    
    this.log('---------- end token expiration ----------');
  }

  /**
   * Refresh the access token with retry logic and coordination
   */
  private refreshToken(): void {
    // Before anything else, including the in-progress check: once the session is over there is
    // nothing to refresh, and asking anyway is how the five-second 401 loop kept itself alive.
    if (this.terminator.isSessionOver()) {
      this.log('[TokenRefreshTimer] Session is over; not refreshing');
      this.stopTimer();
      return;
    }

    if (this.coordinator.isRefreshInProgress()) {
      this.log('[TokenRefreshTimer] Refresh already in progress, skipping');
      return;
    }

    // A refresh that came back without moving the expiry (noteRefreshOutcome) is not retried on the
    // schedule's terms. Every path that re-arms this timer ends up here — the expired-token branch,
    // a visibility change, the cross-tab event the hollow refresh itself fired — so this is the one
    // place the backoff has to be honoured.
    if (Date.now() < this.hollowRetryNotBefore) {
      this.scheduleAt(this.hollowRetryNotBefore, 'refresh is not advancing the expiry');
      return;
    }

    if (!this.authService.isAuthenticated()) {
      this.log('[TokenRefreshTimer] User not authenticated, skipping refresh');
      this.stopTimer();
      return;
    }

    // Guard against timer drift: if the token still has more than 20% of its lifetime
    // remaining, the timer fired too early — recalculate rather than refreshing now.
    const timeUntilExpiration = this.tokenExpiresAt ? this.tokenExpiresAt - Date.now() : 0;
    const issuedAt = this.authService.getAccessTokenIssuedAt();
    const minTimeToRecalculate = (issuedAt && this.tokenExpiresAt)
      ? (this.tokenExpiresAt - issuedAt.getTime()) * (1 - this.REFRESH_THRESHOLD)
      : this.MIN_TIME_REMAINING_TO_RECALCULATE;
    if (timeUntilExpiration > minTimeToRecalculate) {
      this.log('[TokenRefreshTimer] Token still has more than 20% lifetime remaining, recalculating timer');
      this.initializeTimer();
      return;
    }

    const sessionId = this.coordinator.currentSessionId();
    this.metrics.recordAttempt();
    const startTime = Date.now();
    const expiryBefore = this.authService.getAccessTokenExpiresAt()?.getTime() ?? null;

    this.log('[TokenRefreshTimer] Refreshing token automatically');

    // No retry loop here any more. Retries are the coordinator's: it holds one backoff that the
    // interceptor obeys too, so requests arriving during an outage cannot bypass the waiting this
    // timer is doing. The old private loop retried a definitive 401 five times, because it read
    // "no status" as a network error and every error arriving as an ApiError had no `status`.
    firstValueFrom(this.coordinator.getOrStart('timer', sessionId))
      .then(() => {
        this.metrics.recordSuccess(Date.now() - startTime);
        this.noteRefreshOutcome(expiryBefore);
        this.initializeTimer();
      })
      .catch((error: unknown) => {
        if (error instanceof StaleSessionError) {
          this.stopTimer();
          return;
        }

        this.metrics.recordFailure((error as Error)?.message || 'Unknown error');

        if (isTerminalAuthFailure(error)) {
          // The credential was rejected: the session is over, and only the terminator ends it.
          this.stopTimer();
          this.terminator.end('timer-refresh-rejected', sessionId);
          return;
        }

        // Temporary. Keep the session and try again when the shared cooldown allows it.
        const retryAt = error instanceof RefreshCooldownError
          ? error.retryAt
          : this.coordinator.getRetryAt();
        this.scheduleRetryAt(retryAt);
      });
  }

  /**
   * Re-arm after a temporary failure, at the moment the coordinator says we may ask again — which
   * accounts for the server's own `Retry-After` when it sent one.
   */
  private scheduleRetryAt(retryAt: number): void {
    const delay = Math.max(retryAt - Date.now(), this.MIN_REFRESH_INTERVAL);
    this.log(`[TokenRefreshTimer] Refresh failed temporarily; retrying in ${Math.round(delay / 1000)}s`);

    if (this.refreshTimer) {
      clearTimeout(this.refreshTimer);
    }
    this.refreshTimer = setTimeout(() => this.refreshToken(), delay);
  }

  /**
   * Compare the expiry after a refresh with the one before it.
   *
   * A refresh that "succeeded" without moving the expiry is one this timer cannot act on. It
   * happened on the customer app for a whole night: a lazy module listed AuthService in its own
   * `providers`, Angular built a second instance, that instance registered itself as the refresh
   * handler and wrote each new expiry into its own fields — while this timer kept reading the root
   * instance, whose expiry never moved. Every cycle then reasoned, correctly, "the token is spent,
   * refresh now", and rotated a perfectly good session thousands of times (plan §8.12).
   *
   * The instance mix-up is now refused at its source (AuthService's singleton guard), but a timer
   * that cannot tell a hollow success from a real one will loop on the next such mistake too. So a
   * hollow success backs off — doubling from ten seconds up to five minutes — and says so once,
   * loudly. The streak ends the moment an expiry actually advances.
   */
  private noteRefreshOutcome(expiryBefore: number | null): void {
    const expiryAfter = this.authService.getAccessTokenExpiresAt()?.getTime() ?? null;
    if (expiryBefore === null || expiryAfter === null || expiryAfter > expiryBefore) {
      this.hollowRefreshStreak = 0;
      this.hollowRetryNotBefore = 0;
      return;
    }

    this.hollowRefreshStreak++;
    const backoff = Math.min(
      this.MIN_REFRESH_INTERVAL * 2 ** this.hollowRefreshStreak,
      this.MAX_HOLLOW_REFRESH_BACKOFF_MS);
    this.hollowRetryNotBefore = Date.now() + backoff;

    if (this.hollowRefreshStreak === this.HOLLOW_REFRESH_ALARM_AT) {
      console.error(
        `[TokenRefreshTimer] ${this.hollowRefreshStreak} refreshes in a row completed without ` +
        'advancing the access-token expiry this timer reads. Something other than the root ' +
        'AuthService is performing the refresh. Backing off instead of looping.');
    }
    this.log(
      `[TokenRefreshTimer] Refresh did not advance the expiry; not asking again for ` +
      `${Math.round(backoff / 1000)}s`);
  }

  /** Re-arm for a specific moment, whichever scheduling path asked. */
  private scheduleAt(at: number, why: string): void {
    const delay = Math.max(at - Date.now(), this.MIN_REFRESH_INTERVAL);
    this.log(`[TokenRefreshTimer] Waiting ${Math.round(delay / 1000)}s (${why})`);

    if (this.refreshTimer) {
      clearTimeout(this.refreshTimer);
    }
    this.refreshTimer = setTimeout(() => this.refreshToken(), delay);
  }

  /**
   * Listen for token refreshes from other tabs
   * When another tab refreshes tokens, this tab will restart its timer with new expiration
   */
  private setupCrossTabRefreshListener(): void {
    if (typeof window === 'undefined') {
      return; // Not in browser environment
    }

    window.addEventListener('tokenRefreshed', () => {
      this.log('[TokenRefreshTimer] Token refreshed in another tab, restarting timer');
      if (this.authService.isAuthenticated()) {
        // Restart timer with new expiration time
        this.initializeTimer();
      }
    });
  }

  /**
   * Handle page visibility changes to prevent timer drift
   */
  private setupVisibilityHandling(): void {
    if (typeof document === 'undefined') {
      return; // SSR
    }

    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'visible') {
        // Page became visible - recalculate timer to account for time passed
        this.log('[TokenRefreshTimer] Page became visible, recalculating timer');
        if (this.authService.isAuthenticated()) {
          this.initializeTimer();
        }
      }
    });

    // Handle window focus (another indicator of page becoming active)
    window.addEventListener('focus', () => {
      if (this.authService.isAuthenticated()) {
        this.initializeTimer();
      }
    });
  }

  /**
   * Get time until next refresh in milliseconds
   */
  getTimeUntilRefresh(): number | null {
    if (!this.tokenExpiresAt || !this.refreshTimer) {
      return null;
    }

    const now = Date.now();
    const timeUntilExpiration = this.tokenExpiresAt - now;
    if (timeUntilExpiration <= 0) return 0;

    const issuedAt = this.authService.getAccessTokenIssuedAt();
    if (issuedAt) {
      const tokenLifetimeMs = this.tokenExpiresAt - issuedAt.getTime();
      const delay = timeUntilExpiration - tokenLifetimeMs * (1 - this.REFRESH_THRESHOLD);
      return Math.max(delay, 0);
    }

    return Math.max(Math.floor(timeUntilExpiration * this.REFRESH_THRESHOLD), 0);
  }

  /**
   * Manually trigger token refresh (useful for testing or manual refresh)
   */
  manualRefresh(): void {
    this.refreshToken();
  }
}
